import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from 'react-redux';
import "./modulelist.scss";
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Sidebar from "../Sidebar";
import Header from "../../component/loaderComponent/Header/Header";
import { constant, MODULE_VEHICLETYPE } from "../../constants/constant";
import { calculatePageCount } from "../../constants/constant";
import { getVehiclesCategoryAction, getVehiclesByCategoryAction, moduleListAction, getTrainingCategoryAction } from '../../actions/trainingModuleAction';
import { Image_Url } from '../../constants/imageUrl/ImageUrl';
import { HTTP_STATUS } from '../../constants/httpsStatus/httpsStatus';
import { notify } from '../../services/notification';
import useMobileView from "../../constants/useMobileView";
import { is2w } from '../../constants/vehicleTypeHelper';
import AddModuleTableSkeleton from "../../component/SkeletonLoader/AddModuleTableSkeleton";
import NoDataFound from "../../component/noDataComponent/NoDataFound";

const ModuleList = () => {
  const twoW = is2w();

  const [searchQuery, setSearchQuery] = useState('');
  const [count, setCount] = useState(1);
  const [activeTab, setActiveTab] = useState('module');
  const [moduleList, setModuleList] = useState([]);
  const [filteredModules, setFilteredModules] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { isSidebarOpen, toggleSidebar } = useMobileView();
  const [paginationListcount, setpaginationListcount] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [startIndex, setStartIndex] = useState(1);

  // 2W-specific state
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoryFilters, setCategoryFilters] = useState([{ value: '', label: 'All' }]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Route paths
  const contentListPath = twoW ? '/2w/training/list' : '/training/tutorialtroubleshooting';
  const moduleListPath = twoW ? '/2w/training/modules/list' : '/training/modules/list';
  const addModulePath = twoW ? '/2w/training/addmodules' : '/training/addmodules';
  const editModuleBasePath = twoW ? '/2w/training/editmodules' : '/training/editmodules';

  const fetchTrainingCategories = async () => {
    try {
      const response = await dispatch(getTrainingCategoryAction());
      if (response?.status === HTTP_STATUS.SUCCESS && response?.data?.success) {
        const categories = response?.data?.data || [];
        setCategoryFilters([
          { value: '', label: 'All' },
          ...categories.map(cat => ({ value: cat.id, label: cat.display_name }))
        ]);
      }
    } catch (error) {
      notify.error('Failed to fetch training categories');
    }
  };

  useEffect(() => {
    if (!twoW) {
      if (location.pathname === '/modules_list') {
        setActiveTab('module');
      } else if (location.pathname === '/tutorial_troubles_shooting') {
        setActiveTab('tutorial');
      }
    }

    if (twoW) {
      fetchTrainingCategories();
    }
    fetchVehicles();
  }, [location.pathname]);

  const fetchVehicles = async () => {
    try {
      const categoryResp = await dispatch(getVehiclesCategoryAction());
      if (categoryResp?.status === HTTP_STATUS.SUCCESS) {
        const categories = categoryResp.data?.data?.results || [];
        const matchedCategory = categories.find(cat =>
          twoW
            ? cat.name?.toUpperCase().includes(MODULE_VEHICLETYPE.twoWheeler)
            : cat.name?.toUpperCase().includes(MODULE_VEHICLETYPE.threeWheeler)
        );
        if (matchedCategory) {
          const vehicleResp = await dispatch(getVehiclesByCategoryAction(matchedCategory.id));
          if (vehicleResp?.status === HTTP_STATUS.SUCCESS) {
            setVehicles(vehicleResp.data?.data?.results || []);
          }
        }
      }
    } catch (error) {
      setVehicles([]);
    }
  };

  const fetchModuleList = async (currentPage = 1) => {
    setIsLoading(true);
    const newStartIndex = (currentPage - 1) * 10 + 1;
    setStartIndex(newStartIndex);
    try {
      const vehicleId = selectedVehicle || '';
      const response = await dispatch(moduleListAction(currentPage, vehicleId, searchQuery, selectedCategory));

      if (response?.status === HTTP_STATUS.SUCCESS) {
        const { results, count } = response?.data?.data;
        const filteredResults = searchQuery
          ? results.filter(module =>
            module.name.toLowerCase().includes(searchQuery.toLowerCase())
          )
          : results;

        setModuleList(results);
        setFilteredModules(filteredResults);
        setpaginationListcount(count);
        const totalPageCount = calculatePageCount(count, constant.paginationCount);
        setCount(totalPageCount);
      } else {
        setModuleList([]);
        setFilteredModules([]);
        setpaginationListcount(0);
        setCount(1);
      }
    } catch (error) {
      setModuleList([]);
      setFilteredModules([]);
      setpaginationListcount(0);
      setCount(1);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (tab, route) => {
    setActiveTab(tab);
    navigate(route);
  };

  const handleVehicleChange = (e) => {
    setSelectedVehicle(e.target.value);
    setPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery?.length >= 3 || searchQuery?.length === 0) {
      const filtered = moduleList.filter(module =>
        module.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredModules(filtered);
      setPage(1);
      const totalPageCount = calculatePageCount(filtered?.length, constant.paginationCount);
      setCount(totalPageCount);
      setpaginationListcount(filtered?.length);
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.length === 0) {
      setFilteredModules(moduleList);
      setpaginationListcount(moduleList?.length);
      const totalPageCount = calculatePageCount(moduleList?.length, constant.paginationCount);
      setCount(totalPageCount);
    }
  };

  const handlePaginationChange = (event, value) => {
    setPage(value);
  };

  const handleCategoryFilter = (categoryValue) => {
    setSelectedCategory(categoryValue);
    setPage(1);
  };

  useEffect(() => {
    fetchModuleList(page);
  }, [page, selectedVehicle, ...(twoW ? [selectedCategory] : [])]);

  const startItem = moduleList?.length === 0 ? 0 : (page - 1) * constant.paginationCount + 1;
  const endItem = Math.min(page * constant.paginationCount, paginationListcount);

  return (
    <div className={twoW ? "tutorial-page-container-2w-module" : "tutorial-page-container"}>
      <div className="tutorial-content-wrapper">
        <Header title={"Module List"} />
        <div className="tutorial-section">
          <div className="tutorial-panel">
            <div className="tutorial-nav-buttons">
              <div style={{ display: "flex", gap: "1rem" }}>
                <button
                  className={`tutorial-nav-btn ${activeTab === (twoW ? 'training' : 'tutorial') ? 'active' : ''}`}
                  onClick={() => handleTabChange(twoW ? 'training' : 'tutorial', contentListPath)}
                >
                  {twoW ? 'Training & Service' : 'Tutorial and Troubleshooting'}
                </button>
                <button
                  className={`tutorial-nav-btn ${activeTab === 'module' ? 'active' : ''}`}
                  onClick={() => handleTabChange('module', moduleListPath)}
                >
                  Module
                </button>
              </div>
              <button
                className="tutorial-add-btn"
                onClick={() => navigate(addModulePath)}
              >
                Add Module
              </button>
            </div>

            {twoW && (
              <div className="category-filter-tabs">
                {categoryFilters.map((cat) => (
                  <button
                    key={cat.value}
                    className={`category-tab-btn ${selectedCategory === cat.value ? 'active' : ''}`}
                    onClick={() => handleCategoryFilter(cat.value)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            )}

            <div className="tutorial-search">
              <label className="form-label">Select Vehicle</label>
              <select
                className="form-select-dropdown"
                value={selectedVehicle}
                onChange={handleVehicleChange}
              >
                <option value="">All Vehicle</option>
                {vehicles?.map(vehicle => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Search by module name"
                className="tutorial-search-input"
                value={searchQuery}
                onChange={handleSearchInputChange}
              />
              <button
                className="tutorial-add-btn"
                onClick={handleSearch}
                disabled={searchQuery.length > 0 && searchQuery.length < 3}
              >
                Search
              </button>
            </div>

            <div className="tutorial-table-container">
              {isLoading ? (
                <AddModuleTableSkeleton rows={10} />
              ) : filteredModules?.length === 0 ? (
                <NoDataFound />
              ) : (
                <>
                  <div className='module-table-data'>
                    <table className="table">
                      <thead>
                        <tr>
                          <th className="tablesth">S.No.</th>
                          <th className="tablesth">Module Name</th>
                          {twoW && <th className="tablesth">Category</th>}
                          <th className="tablesth">Description</th>
                          <th className="tablesth">Content</th>
                          <th className="tablesth">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredModules?.map((data, ind) => (
                          <tr key={data.id}>
                            <td className="tabletd-modulelist">
                              {ind + startIndex}
                            </td>
                            <td className="tabletd-modulelist">{data?.name}</td>
                            {twoW && <td className="tabletd-modulelist">{data?.training_category || 'N/A'}</td>}
                            <td className="tabletd-modulelist long-text" title={data?.description}>{data?.description}</td>
                            <td className="tabletd-modulelist"><p>Video: {data?.content?.video}</p>
                              <p className="pdf-text">PDF: {data?.content?.pdf}</p></td>
                            <td className={`tabletd-modulelist${twoW ? ' action-icons-cell' : ''}`}>
                              <img
                                src={Image_Url.EDIT}
                                className="training-editicon-module-list"
                                alt="Edit Icon"
                                onClick={() => {
                                  navigate(`${editModuleBasePath}/${data?.id}`);
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="Pagination_section">
              <Stack spacing={2}>
                <Pagination
                  count={count}
                  page={page}
                  size="medium"
                  defaultPage={1}
                  color="primary"
                  onChange={handlePaginationChange}
                />
              </Stack>
              <div className="count">
                <b>
                  <span>Displaying {startItem} to {endItem} out of {paginationListcount}</span>
                </b>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ModuleList;
 